import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, TrendingUp, DollarSign, Award, AlertTriangle } from 'lucide-react';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useAppointmentStats } from '@/hooks/useAppointmentStats';
import { useQuoteMetrics } from '@/hooks/useQuoteMetrics';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface TeamLeaderMetricsProps {
  userId: string;
}

export const TeamLeaderMetrics = ({ userId }: TeamLeaderMetricsProps) => {
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const { teamMembers, loading: teamLoading } = useTeamMembers(userId);
  
  // Get aggregate stats for entire team (no filter)
  const { stats: teamStats, loading: teamStatsLoading } = useAppointmentStats();
  const { metrics: teamQuoteMetrics, loading: teamQuotesLoading } = useQuoteMetrics();
  
  // Get stats for selected member if any
  const { stats: memberStats, loading: memberStatsLoading } = useAppointmentStats(undefined, selectedMemberId || undefined);
  const { metrics: memberQuoteMetrics, loading: memberQuotesLoading } = useQuoteMetrics(selectedMemberId || undefined);

  const loading = teamLoading || teamStatsLoading || teamQuotesLoading;
  const memberLoading = memberStatsLoading || memberQuotesLoading;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-4 w-24 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const atRiskMembers = teamMembers.filter((member) => {
    // Simple heuristic: members with low conversion would need individual stats
    return false; // Placeholder - would need to fetch individual stats for each member
  });

  const statsToShow = selectedMemberId ? memberStats : teamStats;
  const metricsToShow = selectedMemberId ? memberQuoteMetrics : teamQuoteMetrics;

  return (
    <div className="space-y-6">
      {/* Team Member Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">View Team Member Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedMemberId || 'all'} onValueChange={(value) => setSelectedMemberId(value === 'all' ? null : value)}>
            <SelectTrigger className="w-full md:w-[300px]">
              <SelectValue placeholder="Select team member" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Team Members</SelectItem>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.full_name || member.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Metrics Cards */}
      {memberLoading && selectedMemberId ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <Skeleton className="h-8 w-8" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card className="hover:border-accent transition-all">
            <CardHeader className="pb-3">
              <Users className="h-8 w-8 text-accent mb-2" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {selectedMemberId ? 'Appointments' : 'Team Appointments'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsToShow.totalAppointments}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {statsToShow.scheduledCount} scheduled
              </p>
            </CardContent>
          </Card>

          <Card className="hover:border-accent transition-all">
            <CardHeader className="pb-3">
              <TrendingUp className="h-8 w-8 text-accent mb-2" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Conversion Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsToShow.conversionRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground mt-1">
                {statsToShow.closedDeals} deals closed
              </p>
            </CardContent>
          </Card>

          <Card className="hover:border-accent transition-all">
            <CardHeader className="pb-3">
              <DollarSign className="h-8 w-8 text-accent mb-2" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${metricsToShow.totalRevenue.toFixed(0)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {metricsToShow.totalQuotes} quotes
              </p>
            </CardContent>
          </Card>

          <Card className="hover:border-accent transition-all">
            <CardHeader className="pb-3">
              <Award className="h-8 w-8 text-accent mb-2" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Avg Deal Size
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${metricsToShow.averageDealSize.toFixed(0)}</div>
              <p className="text-xs text-green-600 mt-1">
                ${metricsToShow.paidRevenue.toFixed(0)} paid
              </p>
            </CardContent>
          </Card>

          <Card className="hover:border-accent transition-all">
            <CardHeader className="pb-3">
              <AlertTriangle className="h-8 w-8 text-accent mb-2" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                At Risk
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{statsToShow.atRiskCount}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {statsToShow.atRiskPercentage.toFixed(1)}% of total
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Members Table */}
      {!selectedMemberId && (
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Appointments</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No team members assigned yet
                    </TableCell>
                  </TableRow>
                ) : (
                  teamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.full_name || member.email}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{member.role}</Badge>
                      </TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={() => setSelectedMemberId(member.id)}
                          className="text-accent hover:underline text-sm"
                        >
                          View Details
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
